'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { MessageCircle, X, Send, Loader2, MinusCircle } from 'lucide-react';
import { safeStorageGet, safeStorageSet } from '@/lib/ui/safeStorage';

interface ChatMessage {
  message_id: number;
  session_id: number;
  sender_type: 'customer' | 'agent' | 'system';
  sender_name?: string;
  message: string;
  created_at: string;
}

export default function LiveChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Pre-chat lead capture (bot gate). Once a guest provides contact details
  // they're remembered locally so they don't re-enter them on return visits.
  const [lead, setLead] = useState({ name: '', email: '', phone: '' });
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadError, setLeadError] = useState('');
  // Question-first flow: the visitor's first message is held here until they
  // provide contact details, so they type before they see any form.
  const [pendingMessage, setPendingMessage] = useState('');
  // Proactive invite bubble (product pages only).
  const [showInvite, setShowInvite] = useState(false);
  const pathname = usePathname();
  const hasConfiguratorBar = pathname === '/design-studio' || pathname.startsWith('/products/') || pathname === '/cart';
  const mobileInline = pathname === '/checkout' || pathname === '/cart' || pathname === '/design-studio' || pathname.startsWith('/products/');
  const [fieldFocused, setFieldFocused] = useState(false);
  useEffect(() => {
    const update = () => {
      const element = document.activeElement;
      setFieldFocused(!!element?.matches('input, textarea, select, [contenteditable="true"]') && !element.closest('[data-live-chat]'));
    };
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => { document.removeEventListener('focusin', update); document.removeEventListener('focusout', update); };
  }, []);
  const launcherBottom = 'calc(1.5rem + max(var(--sticky-bar-h, 0px), var(--config-bar-h, 0px)) + var(--offer-bar-h, 0px))';
  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => {
      const height = viewport?.height || window.innerHeight;
      const inset = Math.max(0, window.innerHeight - height - (viewport?.offsetTop || 0));
      document.documentElement.style.setProperty('--chat-viewport-h', `${height}px`);
      document.documentElement.style.setProperty('--chat-keyboard-inset', `${inset}px`);
    };
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => { viewport?.removeEventListener('resize', update); viewport?.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, []);
  // NOTE: this initializer runs during RENDER, on every page, for every
  // visitor. It used to touch localStorage directly, which throws when the
  // browser blocks site data (Chrome per-origin block) or rejects writes (iOS
  // Safari private mode, full quota). Because the root layout wraps the entire
  // app in a single ErrorBoundary, that throw replaced the whole site with the
  // boundary's "Something went wrong" card — no header, no products, no
  // checkout — and its Try Again button re-threw immediately. safeStorage*
  // never throws and mirrors the id in memory, so the chat still works for the
  // page view and the site renders normally.
  const [guestId] = useState(() => {
    if (typeof window === 'undefined') return '';
    let id = safeStorageGet('chat_guest_id');
    if (!id) {
      id = Math.random().toString(36).substring(2) + Date.now().toString(36);
      safeStorageSet('chat_guest_id', id);
    }
    return id;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageId = useRef(0);
  const pollInterval = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Restore previously captured lead so returning guests skip the form.
  useEffect(() => {
    try {
      const saved = localStorage.getItem('chat_lead');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.email) {
          setLead({ name: parsed.name || '', email: parsed.email, phone: parsed.phone || '' });
          setLeadCaptured(true);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Proactive invite: on product pages, after a short dwell, nudge the visitor
  // to chat instead of waiting for them to find the bubble. Once per session.
  useEffect(() => {
    if (isOpen) { setShowInvite(false); return; }
    if (!pathname || !pathname.startsWith('/products') || window.matchMedia('(max-width: 1023px)').matches) return;
    try {
      if (sessionStorage.getItem('chat_invite_dismissed')) return;
    } catch { /* ignore */ }
    const t = setTimeout(() => setShowInvite(true), 40000);
    return () => clearTimeout(t);
  }, [pathname, isOpen]);

  const dismissInvite = () => {
    setShowInvite(false);
    try { sessionStorage.setItem('chat_invite_dismissed', '1'); } catch { /* ignore */ }
  };

  // Check for existing session on open
  const checkExistingSession = useCallback(async () => {
    try {
      const res = await fetch(`/api/v2/support/chat/session?guest_id=${guestId}`);
      if (res.ok) {
        const data = await res.json();
        const session = data.data?.session;
        if (session) {
          setSessionId(session.session_id);
          return session.session_id;
        }
      }
    } catch { /* ignore */ }
    return null;
  }, [guestId]);

  // Fetch messages
  const fetchMessages = useCallback(async (sid: number) => {
    try {
      const res = await fetch(`/api/v2/support/chat/messages/${sid}?after=${lastMessageId.current}&guest_id=${encodeURIComponent(guestId)}`);
      if (res.ok) {
        const data = await res.json();
        const newMessages: ChatMessage[] = data.data?.messages || [];
        if (newMessages.length > 0) {
          setMessages(prev => [...prev, ...newMessages]);
          lastMessageId.current = newMessages[newMessages.length - 1].message_id;
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Start polling when session is active
  useEffect(() => {
    if (sessionId && isOpen) {
      fetchMessages(sessionId);
      pollInterval.current = setInterval(() => fetchMessages(sessionId), 4000);
    }
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [sessionId, isOpen, fetchMessages]);

  const handleOpen = async () => {
    setIsOpen(true);
    setIsMinimized(false);
    const existingId = await checkExistingSession();
    if (existingId) {
      lastMessageId.current = 0;
      setMessages([]);
      fetchMessages(existingId);
    }
  };

  const submitLead = () => {
    const name = lead.name.trim();
    const email = lead.email.trim();
    const phone = lead.phone.trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const phoneOk = phone.replace(/\D/g, '').length >= 7;
    if (!name) { setLeadError('Please enter your name.'); return; }
    if (!emailOk) { setLeadError('Please enter a valid email address.'); return; }
    if (!phoneOk) { setLeadError('Please enter a valid phone number.'); return; }
    setLeadError('');
    const captured = { name, email, phone };
    try { localStorage.setItem('chat_lead', JSON.stringify(captured)); } catch { /* ignore */ }
    setLead(captured);
    setLeadCaptured(true);
    // If they typed a question first (question-first flow), send it now.
    if (pendingMessage) {
      const msg = pendingMessage;
      setPendingMessage('');
      startChat(msg, captured);
    }
  };

  const startChat = async (initialMsg: string, leadOverride?: { name: string; email: string; phone: string }) => {
    const l = leadOverride || lead;
    try {
      const res = await fetch('/api/v2/support/chat/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_session_id: guestId,
          message: initialMsg,
          guest_name: l.name,
          guest_email: l.email,
          guest_phone: l.phone,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const sid = data.data?.session_id;
        if (sid) {
          setSessionId(sid);
          lastMessageId.current = 0;
          setMessages([]);
          fetchMessages(sid);
        }
      }
    } catch { /* ignore */ }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();

    // Question-first: hold the first message and ask for contact details.
    if (!sessionId && !leadCaptured) {
      setPendingMessage(msg);
      setInput('');
      return;
    }

    setInput('');

    if (!sessionId) {
      await startChat(msg);
      return;
    }

    setSending(true);
    try {
      await fetch('/api/v2/support/chat/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: msg, guest_id: guestId }),
      });
      // Immediately poll for new messages
      fetchMessages(sessionId);
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const closeChat = async () => {
    if (sessionId) {
      try {
        await fetch(`/api/v2/support/chat/close/${sessionId}?guest_id=${encodeURIComponent(guestId)}`, { method: 'POST' });
      } catch { /* ignore */ }
    }
    setSessionId(null);
    setMessages([]);
    lastMessageId.current = 0;
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <div data-live-chat className={`${mobileInline ? 'relative my-4 flex items-center justify-center lg:fixed lg:bottom-6 lg:my-0 lg:flex-col lg:items-end' : 'fixed flex flex-col items-end'} right-0 lg:right-6 ${mobileInline ? 'z-0 lg:z-50' : 'z-50'} gap-3 ${!mobileInline ? 'right-4' : ''} ${fieldFocused ? 'max-lg:hidden' : ''} ${hasConfiguratorBar ? 'chat-above-configurator' : ''}`} style={mobileInline ? undefined : { bottom: launcherBottom }}>
        {showInvite && (
          <div className="relative max-w-[260px] bg-white rounded-2xl rounded-br-sm shadow-2xl border border-gray-200 px-4 py-3 animate-in fade-in slide-in-from-bottom-2">
            <button
              onClick={dismissInvite}
              className="absolute -top-2 -right-2 flex h-11 w-11 items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full"
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
            <button onClick={handleOpen} className="text-left">
              <p className="text-sm font-semibold text-heading-charcoal mb-0.5">Picking a shade? 👋</p>
              <p className="text-xs text-gray-500">Ask us anything — sizing, colors, or your order. We reply right here.</p>
            </button>
          </div>
        )}
        <button
          onClick={handleOpen}
          className="bg-charcoal hover:bg-charcoal/90 text-white inline-flex items-center rounded-full p-4 shadow-xl transition-all hover:scale-105"
          aria-label="Open live chat"
        >
          <MessageCircle size={24} />
          {mobileInline && <span className="ml-2 lg:hidden">Need help? Chat with us</span>}
        </button>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        style={mobileInline ? undefined : { bottom: launcherBottom }}
        className={`${mobileInline ? 'relative mx-auto my-4 lg:fixed lg:bottom-6 lg:right-6 lg:my-0' : 'fixed right-4 sm:right-6'} ${mobileInline ? 'z-0 lg:z-50' : 'z-50'} bg-charcoal text-white rounded-full px-5 py-3 shadow-xl flex items-center gap-2 hover:bg-charcoal/90 transition-all ${hasConfiguratorBar ? 'chat-above-configurator' : ''}`}
      >
        <MessageCircle size={18} />
        <span className="text-sm font-medium">Chat</span>
        {messages.length > 0 && (
          <span className="bg-sb-gold text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {messages.filter(m => m.sender_type === 'agent').length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div data-live-chat style={{ bottom: 'calc(1.5rem + max(var(--sticky-bar-h, 0px), var(--config-bar-h, 0px)) + var(--chat-keyboard-inset, 0px))', maxHeight: 'min(520px, calc(var(--chat-viewport-h, 100dvh) - 160px))' }} className={`fixed right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-[360px] flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden ${hasConfiguratorBar ? 'chat-above-configurator' : ''}`}>
      {/* Header */}
      <div className="bg-charcoal text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} />
          <div>
            <h3 className="text-sm font-semibold">Live Chat</h3>
            <p className="text-xs text-white/90">
              {sessionId ? 'Connected' : 'Start a conversation'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsMinimized(true)} className="flex h-11 w-11 items-center justify-center hover:bg-white/20 rounded" aria-label="Minimize">
            <MinusCircle size={16} />
          </button>
          <button onClick={closeChat} className="flex h-11 w-11 items-center justify-center hover:bg-white/20 rounded" aria-label="Close">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[80px] max-h-[360px] bg-gray-50">
        {/* Greeting — shown first so a cold visitor can just type a question. */}
        {!sessionId && !leadCaptured && !pendingMessage && (
          <div className="text-center py-8">
            <MessageCircle size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-600 font-medium">Hi there 👋</p>
            <p className="text-sm text-gray-600 mt-1">Ask us anything about blinds, sizing, colors, or an order — type below and we'll reply here.</p>
          </div>
        )}

        {/* Question-first lead capture — only after they've typed a question. */}
        {!leadCaptured && !sessionId && pendingMessage && (
          <div className="py-2">
            <div className="bg-charcoal text-white text-sm rounded-lg rounded-br-none px-3 py-2 max-w-[85%] ml-auto mb-3">
              {pendingMessage}
            </div>
            <p className="text-sm text-gray-700 font-medium mb-1">Where should we send our reply?</p>
            <p className="text-xs text-gray-500 mb-3">So we can get back to you even if you leave the page. You'll also get an email confirmation.</p>
            <div className="space-y-2">
              <input maxLength={80}
                type="text"
                value={lead.name}
                onChange={(e) => setLead({ ...lead, name: e.target.value })}
                placeholder="Your name"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-charcoal"
              />
              <input maxLength={254}
                type="email"
                value={lead.email}
                onChange={(e) => setLead({ ...lead, email: e.target.value })}
                placeholder="Email address"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-charcoal"
              />
              <input maxLength={20}
                type="tel"
                value={lead.phone}
                onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                placeholder="Phone number"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-charcoal"
              />
              {leadError && <p className="text-xs text-red-600">{leadError}</p>}
              <button
                onClick={submitLead}
                className="w-full bg-charcoal text-white text-sm font-medium py-2 rounded-lg hover:bg-charcoal/90 transition-colors"
              >
                Send my question
              </button>
            </div>
          </div>
        )}
        {messages.length === 0 && leadCaptured && !sessionId && !pendingMessage && (
          <div className="text-center py-8">
            <MessageCircle size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 font-medium">Welcome back{lead.name ? `, ${lead.name}` : ''}!</p>
            <p className="text-sm text-gray-600 mt-1">Type a message to pick up with our team.</p>
          </div>
        )}
        {messages.length === 0 && sessionId && (
          <div className="text-center py-8">
            <Loader2 size={24} className="mx-auto text-gray-300 animate-spin mb-2" />
            <p className="text-xs text-gray-400">Waiting for an agent...</p>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.message_id}
            className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : msg.sender_type === 'system' ? 'justify-center' : 'justify-start'}`}
          >
            {msg.sender_type === 'system' ? (
              <span className="text-xs text-gray-600 bg-gray-100 rounded-full px-3 py-1">
                {msg.message}
              </span>
            ) : (
              <div
                className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                  msg.sender_type === 'customer'
                    ? 'bg-charcoal text-white rounded-br-none'
                    : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                }`}
              >
                {msg.sender_type === 'agent' && msg.sender_name && (
                  <p className="text-xs font-semibold text-gray-700 mb-0.5">{msg.sender_name}</p>
                )}
                <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                <p className={`text-xs mt-0.5 ${msg.sender_type === 'customer' ? 'text-white/80' : 'text-gray-600'}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-2 flex-shrink-0 bg-white">
        <div className="flex items-center gap-2">
          <input maxLength={80}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={pendingMessage && !leadCaptured ? 'Add your details above…' : 'Ask us anything…'}
            disabled={!!pendingMessage && !leadCaptured}
            aria-label="Chat message"
            className="min-h-11 min-w-0 flex-1 px-3 py-2 text-base border border-gray-200 rounded-full focus:outline-none focus:ring-1 focus:ring-charcoal disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            onClick={sendMessage}
            disabled={(!!pendingMessage && !leadCaptured) || !input.trim() || sending}
            aria-label="Send message"
            className="flex h-11 w-11 shrink-0 items-center justify-center bg-charcoal text-white rounded-full hover:bg-charcoal/90 disabled:opacity-40 transition-colors"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
